import { useEffect } from 'react';

import { Button, SubmitButton, Typography } from '@capsule/consumer-design-system';

import type { FormikProps } from 'formik';
import { Formik } from 'formik';
import { useHistory } from 'react-router';
import { css } from 'styled-components';
import * as Yup from 'yup';

import { SimpleFeatureLayout } from 'src/app/components/FeatureLayouts/SimpleFeatureLayout/SimpleFeatureLayout';
import type { HealthProfileMedicationToVerifyGroup } from 'src/app/hooks/data/healthProfile';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { loggedInHealthProfile } from 'src/app/router/paths';
import type { MedicationSearchResult } from 'src/app/types';
import { FORM_VALIDATION_ERRORS } from 'src/consts';

import type { HealthProfileFormProps } from '..';
import { HealthProfileUpdateStepNames } from '../constants';
import type { MedicationsFormValueType } from '../MedicationPicker';
import { MedicationSearchPicker } from '../medicationSearchPicker';
import { NegativeMarginOffset } from '../styles';
import { convertSearchToHealthProfileMedication, getMedicationsTransferEligibility } from '../utils';

const FORM_ID = 'medicationSearchForm';

const validationSchema = Yup.object().shape({
  form: Yup.array().test('otherMeds', FORM_VALIDATION_ERRORS.searchMedications, function () {
    return this.parent.otherMeds.length > 0;
  }),
});

export const SearchMedications = (
  props: HealthProfileFormProps & {
    zipCode?: string;
    healthProfileMedications?: HealthProfileMedicationToVerifyGroup[];
  }
) => {
  const stepName = HealthProfileUpdateStepNames.SearchMedications;
  const history = useHistory();
  const {
    onToggleHideHeader,
    setHealthProfilePersistentData,
    healthProfilePersistentData,
    zipCode,
    updateMedications,
    healthProfileMedications,
  } = props;

  useEffect(() => {
    onToggleHideHeader(true);
  }, [onToggleHideHeader]);

  useSegmentEvent('viewHealthProfileAreThereOtherMeds', {
    shouldTrackOnMount: true,
  });

  const handleSubmit = async (values: { otherMeds: MedicationSearchResult[] }): Promise<void> => {
    const otherMedsSelected = values.otherMeds?.map((med: MedicationSearchResult) =>
      convertSearchToHealthProfileMedication(med)
    );

    const allSelectedMeds = [...healthProfilePersistentData.selectedMeds, ...otherMedsSelected];
    healthProfilePersistentData.selectedMeds = allSelectedMeds;
    await setHealthProfilePersistentData(healthProfilePersistentData);

    updateMedications();

    const { eligibleMeds } = getMedicationsTransferEligibility(healthProfilePersistentData.selectedMeds);

    if (eligibleMeds.length > 0) {
      history.push(loggedInHealthProfile.toPath({ stepName: HealthProfileUpdateStepNames.ConfirmMoveMedications }));
    } else {
      history.push(loggedInHealthProfile.toPath({ stepName: HealthProfileUpdateStepNames.GotIt }));
    }
  };

  return (
    <Formik
      validationSchema={validationSchema}
      id={FORM_ID}
      onSubmit={handleSubmit}
      initialValues={{ otherMeds: [] as MedicationSearchResult[] }}
      enableReinitialize
    >
      {(formik: FormikProps<MedicationsFormValueType>) => {
        const { isSubmitting, errors, submitCount } = formik;

        return (
          <>
            <SimpleFeatureLayout
              css={css`
                margin-top: ${({ theme }) => theme.space.s6};
              `}
              shouldDisplayCapsuleLogo
              backButton={{ isRendered: false }}
              title={
                <Typography
                  variant={{ base: 'displayXl', smallDown: 'heading2' }}
                  css={css`
                    margin-top: ${({ theme }) => theme.space.s6};
                  `}
                >
                  Are there any other medications you currently take?
                </Typography>
              }
              description={` `} // Empty description to keep spacing consistent
              keyName={stepName}
              body={{
                content: (
                  <>
                    <NegativeMarginOffset />
                    <MedicationSearchPicker
                      formik={formik}
                      zipCode={zipCode || ''}
                      formId={FORM_ID}
                      hasAnyFormMeds={!!healthProfileMedications?.map(group => group.medications).flat().length}
                    />
                    <div
                      css={css`
                        display: ${errors.form && submitCount > 0 ? 'block' : 'none'};
                        color: ${({ theme }) => theme.colors.capsuleRed60};
                      `}
                    >
                      <Typography variant="small">Please enter at least one other medication.</Typography>
                    </div>
                    <SubmitButton variant="contained-secondary-lg" isSubmitting={isSubmitting} form={FORM_ID}>
                      Submit
                    </SubmitButton>

                    <Button
                      variant="text-md"
                      onClick={() => {
                        updateMedications();
                        history.push(loggedInHealthProfile.toPath({ stepName: HealthProfileUpdateStepNames.GotIt }));
                      }}
                    >
                      No, I don’t take any meds
                    </Button>
                  </>
                ),
              }}
              error={errors?.otherMeds}
            />
          </>
        );
      }}
    </Formik>
  );
};
